import { Bone, Euler, Object3D, Quaternion, SkinnedMesh, Vector3 } from "three";
import { AvatarRig, resolveRigNode } from "./rig";
export class AvatarSocialMotion {
  private channels = new Map<string, { mesh: SkinnedMesh; index: number }[]>();
  private savedWeights = new Map<SkinnedMesh, Map<number, number>>();
  private savedBones = new Map<Bone, Quaternion>();
  private elapsed = 0;
  private nextBlink: number;
  private blinkStart = -1;
  private randomSeed: number;
  private jaw = 0;
  private yaw = 0;
  private pitch = 0;
  constructor(
    private rig: AvatarRig,
    root: Object3D,
    seed: string,
  ) {
    this.randomSeed = 2166136261;
    for (const c of seed)
      this.randomSeed = Math.imul(this.randomSeed ^ c.charCodeAt(0), 16777619);
    this.nextBlink = 1 + this.random() * 4;
    for (const [key, bindings] of Object.entries(rig.manifest.face))
      this.channels.set(
        key,
        bindings.flatMap((b) => {
          const mesh = resolveRigNode(root, b.path) as SkinnedMesh | undefined;
          return mesh?.morphTargetInfluences ? [{ mesh, index: b.index }] : [];
        }),
      );
  }
  private random() {
    this.randomSeed = (Math.imul(this.randomSeed, 1664525) + 1013904223) >>> 0;
    return this.randomSeed / 4294967296;
  }
  clear() {
    for (const [mesh, weights] of this.savedWeights)
      for (const [index, value] of weights)
        if (mesh.morphTargetInfluences)
          mesh.morphTargetInfluences[index] = value;
    this.savedWeights.clear();
    for (const [bone, q] of this.savedBones) bone.quaternion.copy(q);
    this.savedBones.clear();
  }
  private weight(channel: string, value: number) {
    for (const { mesh, index } of this.channels.get(channel) || []) {
      const weights = mesh.morphTargetInfluences!;
      let saved = this.savedWeights.get(mesh);
      if (!saved) {
        saved = new Map();
        this.savedWeights.set(mesh, saved);
      }
      if (!saved.has(index)) saved.set(index, weights[index]);
      weights[index] = Math.max(weights[index], Math.min(1, value));
    }
  }
  apply(
    root: Object3D,
    delta: number,
    voice: number,
    lookTarget?: Vector3,
    preview?: { blink: boolean; expressions: Record<string, number> },
  ) {
    this.clear();
    const dt = Math.max(0, Math.min(0.1, delta));
    this.elapsed += dt;
    if (this.elapsed >= this.nextBlink) {
      this.blinkStart = this.elapsed;
      this.nextBlink = this.elapsed + 2.5 + this.random() * 3.5;
    }
    const phase = (this.elapsed - this.blinkStart) / 0.14;
    const blink =
      this.blinkStart >= 0 && phase >= 0 && phase <= 1
        ? Math.sin(phase * Math.PI)
        : 0;
    if (this.rig.manifest.capabilities.blink && preview?.blink !== false) {
      this.weight("blinkLeft", blink);
      this.weight("blinkRight", blink);
    }
    const amount = Number.isFinite(voice)
      ? Math.max(0, Math.min(1, voice * 3))
      : 0;
    this.jaw += (amount - this.jaw) * (1 - Math.exp(-18 * dt));
    // Jaw bones have unverified axes. Only drive a mapped mouth morph here.
    this.weight("jawOpen", this.jaw * 0.7);
    for (const [channel, value] of Object.entries(preview?.expressions || {})) {
      if (Number.isFinite(value))
        this.weight(channel, Math.max(0, Math.min(1, value)));
    }
    const head = this.rig.bones.get("head");
    if (!lookTarget || !head || !this.rig.manifest.capabilities.bodyMapped) {
      this.yaw = 0;
      this.pitch = 0;
      return;
    }
    root.updateWorldMatrix(true, true);
    const rootQ = root.getWorldQuaternion(new Quaternion());
    const direction = lookTarget
      .clone()
      .sub(head.getWorldPosition(new Vector3()))
      .applyQuaternion(rootQ.clone().invert());
    const yaw = Math.max(
      -0.65,
      Math.min(0.65, Math.atan2(direction.x, direction.z)),
    );
    const pitch = Math.max(
      -0.3,
      Math.min(
        0.3,
        -Math.atan2(direction.y, Math.hypot(direction.x, direction.z)),
      ),
    );
    this.yaw += (yaw - this.yaw) * (1 - Math.exp(-5 * dt));
    this.pitch += (pitch - this.pitch) * (1 - Math.exp(-5 * dt));
    for (const [name, weight] of [
      ["head", 0.5],
      ["leftEye", 0.7],
      ["rightEye", 0.7],
    ] as const) {
      const bone = this.rig.bones.get(name);
      if (!bone) continue;
      this.savedBones.set(bone, bone.quaternion.clone());
      const deltaQ = rootQ
        .clone()
        .multiply(
          new Quaternion().setFromEuler(
            new Euler(this.pitch * weight, this.yaw * weight, 0, "YXZ"),
          ),
        )
        .multiply(rootQ.clone().invert());
      const desired = deltaQ.multiply(
        bone.getWorldQuaternion(new Quaternion()),
      );
      const parent =
        bone.parent?.getWorldQuaternion(new Quaternion()) || new Quaternion();
      bone.quaternion.copy(parent.invert().multiply(desired));
      bone.updateWorldMatrix(false, true);
    }
  }
}
